import _ from 'lodash';
import { Trans } from 'react-i18next';
import { useSnackbar, closeSnackbar } from 'notistack';
import { Link, useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect, ChangeEvent, useCallback } from 'react';

import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  Box,
  Tab,
  Card,
  Tabs,
  Button,
  Divider,
  MenuItem,
  Skeleton,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useParams, useRouter } from 'src/routes/hooks';

import { useAuth } from 'src/hooks/auth/use-auth';
import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import {
  ClientApplicationStages,
  ApplicationStageDivergence,
  RecruiterApplicationStages,
} from 'src/utils/applications-kanban';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { PageOptions } from 'src/services/api.types';
import { DocumentType } from 'src/services/files/file.types';
import { openCandidateViewer } from 'src/store/slices/app/appSlice';
import { setShowOfferModal } from 'src/store/slices/applications/applicationsSlice';
import { openInterviewSchedulerModal } from 'src/store/slices/interviews/interviewSlice';
import { useGetProfileDocumentsQuery } from 'src/services/candidates/candidates.service';
import {
  RejectionReason,
  ApplicationStage,
  WithdrawalContext,
  RepresentationStatus
} from 'src/services/applications/applications.types';
import {
  getUpdateStageErrorMessage,
  getSaveToProfileErrorMessage,
  getConfirmStageChangeContentMessage,
  getConfirmStageChangeRequestContentMessage
} from 'src/services/applications/applications.utils';
import {
  useGetApplicationByIdQuery,
  useSaveDocumentToProfileMutation,
  useUpdateApplicationRatingMutation,
  useProgressCandidateApplicationMutation,
  useRequestApplicationStageChangeMutation,
  useGetOutgoingApplicationResourceRequestsQuery
} from 'src/services/applications/applications.service';

import Iconify from 'src/components/iconify';
import LinkedInLink from 'src/components/linkedin-link';
import { usePopover } from 'src/components/custom-popover';
import StarRating from 'src/components/rating/star-rating';
import { ConfirmDialog } from 'src/components/custom-dialog';
import DocumentViewer from 'src/components/documents/document-viewer';
import CustomPopover from 'src/components/custom-popover/custom-popover';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import WithdrawalContextAlert from 'src/sections/jobs/applications/alerts/withdrawal-alert';
import CandidateProfileDocumentsView from 'src/sections/profile/documents/profile-documents';
import ApplicationReferencesView from 'src/sections/jobs/applications/detail/application-references-view';
import RequestReferenceModal from 'src/sections/jobs/applications/modals/references/reference-request-modal';
import ApplicationScreeningSubmissionView from 'src/sections/jobs/applications/detail/screening-submissions-view';
import ApplicationProfileImporterDialog from 'src/sections/jobs/applications/modals/application-profile-importer';
import ReferenceRequestListModal from 'src/sections/jobs/applications/modals/references/reference-request-list-modal';

import { TenantType } from 'src/types/enums';

import OffersListModal from '../modals/offer/offers-list-modal';
import ApplicationActionMenu from '../../menus/application-action-menu';
import InterviewListModal from '../modals/interview/interview-list-modal';
import ApplicationCandidateProfile from './application-candidate-profile';
import InterviewPhaseModal from '../modals/interview/interview-phase-modal';
import ApplicationRejectionConfirm from '../modals/application-reject-confim';
import { ApplicationRepresentationStatusIcon } from '../representation-status-icon';
import ResourceRequestModal from '../modals/resource-requests/resource-request-modal';
import ResourceRequestsListModal from '../modals/resource-requests/resource-requests-list-modal';

enum TabValue {
  PROFILE = 'profile',
  RESUME = 'resume',
  DOCUMENTS = 'documents',
  SCREENING = 'screening_questions',
  REFERENCES = 'references'
}

export default function ApplicationDetailView() {

  const { application_id: applicationId, job_id } = useParams();

  const { t } = useTranslate();

  const { context } = useAuth();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchParams, setSearchParams] = useSearchParams();

  const stagePopover = usePopover();

  const rejectApplicationControl = useBoolean();

  const showActions = usePopover();

  const profileImporterControl = useBoolean();

  const confirmStageChange = useBoolean();

  const confirmRequestStageChange = useBoolean();

  const infoRequestInitiateModalControl = useBoolean();

  const referenceInitiateModalControl = useBoolean();

  const { enqueueSnackbar } = useSnackbar();

  const tenantType = useOrgTenant();

  const showOffersModal = useBoolean();

  const showOutgoingResourceRequests = useBoolean();

  const showInterviewPhaseModal = useBoolean();

  const interviewListModalControl = useBoolean();
  const referenceRequestListModalControl = useBoolean(false);

  const dispatch = useAppDispatch();

  const router = useRouter();

  const [resourceRequestPageOptions, setResourceRequestPageOptions] = useState<PageOptions>({
    page: 1,
    per_page: 5,
  });

  const [currentTab, setCurrentTab] = useState<TabValue | undefined>();

  const [requestedStage, setRequestedStage] = useState<ApplicationStage | null>(null);

  const { currentData: resourceRequests } = useGetOutgoingApplicationResourceRequestsQuery(
    {
      applicationId: applicationId as string,
      params: {
        ...resourceRequestPageOptions,
      },
    },
    { skip: !applicationId }
  );

  const [updateApplicationRating, { isLoading: updatingRating }] = useUpdateApplicationRatingMutation();

  const [saveDocToProfile, { isLoading: isSavingToProfile }] = useSaveDocumentToProfileMutation();

  const onChangeResourceRequestPage = useCallback((e: any, value: number) => {
    setResourceRequestPageOptions((prev) => ({
      ...prev,
      page: value,
    }));
  }, []);

  const { currentData: application, isLoading } = useGetApplicationByIdQuery(
    applicationId as string,
    {
      skip: !applicationId,
    }
  );

  const { currentData: applicationDocuments, isLoading: isLoadingDocuments } = useGetProfileDocumentsQuery(
    {
      profileId: application?.attached_profile.id as string,
      params: { page: 1, per_page: 10 }
    },
    { skip: !application?.attached_profile.id }
  );

  const mustRequestStageChange = useMemo(
    () => application?.manager_type === TenantType.Recruiter && tenantType === TenantType.Client,
    [application?.manager_type, tenantType]
  );

  const canScheduleInterview = tenantType !== TenantType.Candidate;

  // Set default tab
  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (currentTab) return;

    let defaultTab: TabValue = TabValue.PROFILE

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(TabValue).includes(existingTab as TabValue)) {
      defaultTab = existingTab as TabValue;
    }

    setCurrentTab(defaultTab);

  }, [currentTab, tenantType, searchParams]);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!currentTab) return;

    setSearchParams({ "tab": currentTab }, { replace: true })

  }, [currentTab, setSearchParams]);

  // Redirect to job application if job_id is missing (possible link issue)
  useEffect(() => {
    if (application && job_id === 'undefined') {
      console.log('Redirecting to job application with job id');
      router.replace(paths.dashboard.jobs.application(application.job.id, application.id));
    }
  }, [job_id, application, router]);

  const onBack = useCallback(() => {
    router.back();
  }, [router]);

  const [progressApplication, { isLoading: isProgressing }] =
    useProgressCandidateApplicationMutation();

  const [requestStageChange, { isLoading: isRequesting }] =
    useRequestApplicationStageChangeMutation();

  const handleChangeTab = (_event: ChangeEvent<{}>, newValue: TabValue) => {
    setCurrentTab(newValue);
  };

  const onOpenInterviewModal = useCallback(() => {
    dispatch(openInterviewSchedulerModal({ applicationId: applicationId as string }));
  }, [applicationId, dispatch]);

  const stageDisabled = (itemStage: ApplicationStage, currentApplicationStage: ApplicationStage) =>
    RecruiterApplicationStages.findIndex((column) => column === itemStage) <
    RecruiterApplicationStages.findIndex((column) => column === currentApplicationStage) ||
    itemStage === currentApplicationStage ||
    (ClientApplicationStages.includes(currentApplicationStage) &&
      ApplicationStageDivergence.includes(itemStage));


  const onExtendOffer = useCallback(() => {
    if (!applicationId) return;

    dispatch(setShowOfferModal({ applicationId: applicationId as string }));
  },
    [dispatch, applicationId]
  );

  const resumeDocument = useMemo(() => {

    if (applicationDocuments && applicationDocuments.results.length > 0) {
      // sort documents by latest
      const sorted = [...applicationDocuments.results].sort((a, b) => new Date(b.document.created_at).getTime() - new Date(a.document.created_at).getTime());
      return sorted.find(doc => doc.document.type === DocumentType.RESUME);
    }

    return null;
  }, [applicationDocuments]);

  const handleProgressStage = useCallback((stage: ApplicationStage) => {

    setRequestedStage(stage as ApplicationStage);

    switch (stage) {
      case ApplicationStage.OFFER:
        onExtendOffer();
        break;
      case ApplicationStage.REJECTED:
        rejectApplicationControl.onTrue();
        break;
      default:
        if (mustRequestStageChange) {
          confirmRequestStageChange.onTrue();
        }
        else {
          confirmStageChange.onTrue();
        }
        break;
    }

    stagePopover.onClose();
  }, [
    confirmRequestStageChange,
    confirmStageChange,
    mustRequestStageChange,
    rejectApplicationControl,
    onExtendOffer,
    stagePopover
  ]);

  const onProgressApplication = useCallback(
    async (stage: ApplicationStage) => {
      stagePopover.onClose();

      confirmStageChange.onFalse();

      try {
        await progressApplication({
          applicationId: applicationId as string,
          data: { stage: stage as ApplicationStage },
        }).unwrap();

        enqueueSnackbar(t('applications.api.update_stage.success'));
      } catch (e) {
        console.error(e);

        enqueueSnackbar(getUpdateStageErrorMessage(e), { variant: 'error' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [progressApplication]
  );

  const onRequestProgressApplication = useCallback(
    async (stage: ApplicationStage) => {
      confirmRequestStageChange.onFalse();
      try {
        await requestStageChange({
          applicationId: applicationId as string,
          requested_stage: stage,
        }).unwrap();

        enqueueSnackbar(t('applications.api.update_stage.success'));
      } catch (e) {
        console.error(e);

        enqueueSnackbar(getUpdateStageErrorMessage(e), { variant: 'error' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestStageChange]
  );

  const viewCandidate = useCallback(() => {
    if (!application?.org_candidate?.id) return;

    dispatch(openCandidateViewer({ candidateId: application?.org_candidate?.id as string }));
  }, [application, dispatch]);

  const onRejectApplication = useCallback(
    async (reason: RejectionReason, message: string, shouldNotify: boolean) => {

      if (mustRequestStageChange) {
        try {
          await requestStageChange({
            applicationId: applicationId as string,
            requested_stage: ApplicationStage.REJECTED,
            rejection_reason: reason,
            rejection_feedback: message,
          }).unwrap();

          enqueueSnackbar(t('applications.api.update_stage.success'));
        } catch (e) {
          console.error(e);

          enqueueSnackbar(getUpdateStageErrorMessage(e), { variant: 'error' });
        }
      } else {
        try {
          await progressApplication({
            applicationId: applicationId as string,
            data: {
              stage: ApplicationStage.REJECTED as ApplicationStage,
              rejection_context: {
                reason,
                feedback: message,
                should_notify: shouldNotify
              },
            },
          }).unwrap();

          enqueueSnackbar(t('applications.api.update_stage.success'));
        } catch (e) {
          console.error(e);

          enqueueSnackbar(getUpdateStageErrorMessage(e), { variant: 'error' });
        }
      }

      rejectApplicationControl.onFalse();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      rejectApplicationControl,
      mustRequestStageChange,
      requestStageChange,
    ]
  );

  const onCancelRejectApplication = useCallback(() => {
    rejectApplicationControl.onFalse();
  }, [rejectApplicationControl]);

  const renderHead = (
    <>
      <Grid xs={12} sm={12} md={12} lg={4}>
        <Stack direction="column" gap={1}>
          <Box>
            <Button
              fullWidth={false}
              component={RouterLink}
              onClick={onBack}
              startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={16} />}
            >
              {t('common.back')}
            </Button>
          </Box>

          {isLoading ? (
            <Skeleton variant="text" width={400} height={60} />
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>

              <Link
                to={paths.dashboard.candidates.detail(application?.org_candidate?.id as string)}
                style={{
                  color: theme.palette.text.primary,
                  textDecoration: 'none'
                }}
              >
                <TruncatedTextField
                  text={`${application?.attached_profile.first_name} ${application?.attached_profile.last_name}`}
                  mode="characters"
                  limit={30}
                  variant="h3"
                  flexWrap="nowrap"
                  style={{ textTransform: 'capitalize' }}
                />
              </Link>
              <ApplicationRepresentationStatusIcon
                status={application?.representation_status as RepresentationStatus}
                size={25}
              />
              {/* Only sent to the manager org, the same gate as email/phone (JT1-1480). */}
              <LinkedInLink url={application?.org_candidate?.linkedin_url} size={20} />
            </Stack>
          )}

          {/* <Typography variant="subtitle1" color={theme.palette.grey[600]}>
            placeholder@gmail.com
          </Typography> */}
        </Stack>
      </Grid>
      <Grid
        xs={12}
        sm={12}
        md={12}
        lg={8}
        display="flex"
        alignItems="flex-end"
        justifyContent={{ md: 'space-between', lg: 'flex-end' }}
      >
        {application?.stage && (
          <Box sx={{ width: '100%' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              height="100%"
              width="100%"
              spacing={1}
              justifyContent={{ xs: 'center', sm: 'flex-end' }}
            >

              {
                application?.org_candidate?.id && (
                  <Button startIcon={<Iconify icon="solar:eye-bold-duotone" />} variant="contained" onClick={viewCandidate}>
                    {t('applications.detail.actions.view_candidate')}
                  </Button>
                )
              }

              {
                ((application?.sent_to_client_at === null && application?.manager_type === TenantType.Recruiter) ||
                  application?.manager_type === TenantType.Client) && (
                  <Button startIcon={<Iconify icon="solar:upload-bold" />} variant="contained" onClick={profileImporterControl.onTrue}>
                    {t('applications.detail.actions.import_profile')}
                  </Button>
                )
              }

              <LoadingButton
                disabled={isLoading}
                loading={isProgressing || isRequesting}
                onClick={showActions.onOpen}
                variant="contained"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {`${t('applications.detail.actions.button_text')}`}
                  <Iconify sx={{ display: 'inline-block' }} icon="eva:arrow-ios-downward-fill" />
                </Stack>
              </LoadingButton>

              <CustomPopover
                open={showActions.open}
                onClose={showActions.onClose}
                arrow="top-right"
              >
                <ApplicationActionMenu
                  anchorEl={showActions.open}
                  applicationId={application?.id as string}
                  closeMenu={showActions.onClose}
                  onOpenResourceRequestsModal={infoRequestInitiateModalControl.onTrue}
                  onOpenOutgoingResourceRequestsModal={showOutgoingResourceRequests.onTrue}
                  onOpenReferenceInitiateModal={referenceInitiateModalControl.onTrue}
                  onOpenReferenceRequestListModal={referenceRequestListModalControl.onTrue}
                  onOpenInterviewListModal={interviewListModalControl.onTrue}
                  onOpenOfferListModal={showOffersModal.onTrue}
                  onOpenInterviewPhaseModal={showInterviewPhaseModal.onTrue}
                  onOpenRequestStageChangeConfirm={(stage) => {
                    setRequestedStage(stage);
                    confirmRequestStageChange.onTrue();
                  }}
                  onOpenStageChangeConfirm={(stage) => {
                    setRequestedStage(stage);
                    confirmStageChange.onTrue();
                  }}
                />
              </CustomPopover>

              <LoadingButton
                loading={isProgressing || isRequesting}
                disabled={
                  isLoading || !applicationId || !application || application.requested_stage
                }
                variant="contained"
                onClick={stagePopover.onOpen}
                endIcon={
                  <Iconify
                    sx={{ display: 'inline-block' }}
                    icon="eva:arrow-ios-downward-fill"
                  />
                }
              >
                {`${t('common.stage')}: ${t(
                  `enums.application_stage.${application?.requested_stage ?? application?.stage
                  }`
                )}`}
              </LoadingButton>
            </Stack>
          </Box>
        )}
      </Grid>
    </>
  );

  const renderTabs = (
    <Box
      sx={{
        p: 0,
        overflowX: 'auto',
        maxWidth: '100vw',
        WebkitOverflowScrolling: 'touch',  // smooth iOS scroll
      }}
    >
      <Tabs value={currentTab} onChange={handleChangeTab} sx={{ pl: { md: 2 } }}>

        <Tab
          key={TabValue.PROFILE}
          iconPosition="end"
          value={TabValue.PROFILE}
          label={t('applications.detail.tabs.profile')}
        />

        {
          resumeDocument && (
            <Tab
              key={TabValue.RESUME}
              value={TabValue.RESUME}
              label={t('applications.detail.tabs.resume')}
            />
          )
        }

        <Tab
          key={TabValue.DOCUMENTS}
          value={TabValue.DOCUMENTS}
          label={t('applications.detail.tabs.documents')}
        />

        <Tab
          key={TabValue.SCREENING}
          value={TabValue.SCREENING}
          label={t('applications.detail.tabs.compliance_submissions')}
        />

        <Tab
          key={TabValue.REFERENCES}
          value={TabValue.REFERENCES}
          label={t('applications.detail.tabs.references')}
        />

      </Tabs>
    </Box>
  );

  const getClientThread = () => {
    if (application?.manager_client_thread) {
      if (
        (tenantType === application.manager_type &&
          application.manager_type === TenantType.Recruiter) ||
        tenantType === TenantType.Client
      ) {
        return application.manager_client_thread.id;
      }
    }

    return undefined;
  };

  const getCandidateThread = () => {
    if (application?.manager_candidate_thread) {
      if (tenantType === application.manager_type || tenantType === TenantType.Candidate) {
        return application.manager_candidate_thread.id;
      }
    }

    return undefined;
  };

  const handleRatingChange = useCallback(async (id: string, rating: number) => {
    try {
      await updateApplicationRating({ applicationId: id, rating }).unwrap();

      enqueueSnackbar(t('applications.api.update_rating.success'), { variant: 'success' });
    }
    catch (error) {
      console.error(error);

      enqueueSnackbar(t('applications.api.update_rating.default_error'), { variant: 'error' });
    }
  }, [updateApplicationRating, t, enqueueSnackbar]);

  const handleSaveToProfile = useCallback(async (documentId: string) => {

    if (!application) return;

    const toastId = enqueueSnackbar(t('applications.api.save_to_profile.loading'), {
      variant: 'loading',
      persist: true
    });

    try {
      await saveDocToProfile({
        applicationId: application?.id as string,
        documentId,
      }).unwrap();

      closeSnackbar(toastId);
      enqueueSnackbar(t('applications.api.save_to_profile.success'), { variant: 'success' });
    }
    catch (error) {
      closeSnackbar(toastId);
      enqueueSnackbar(getSaveToProfileErrorMessage(error), { variant: 'error' });
    }
  }, [t, enqueueSnackbar, saveDocToProfile, application]);

  return (
    <>
      <Grid container spacing={3}>

        {renderHead}

        {(application?.manager_type === TenantType.Recruiter && application?.recruiter && tenantType === TenantType.Client) && (
          <Grid xs={12}>
            <Card sx={{ p: 2 }} variant="outlined">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="solar:star-bold-duotone" color="orange" width={24} height={24} />
                <Typography variant="subtitle2">
                  <Trans
                    components={[<Link to={paths.dashboard.companies.detail(application?.recruiter.company_profile.id, tenantType, TenantType.Recruiter)} />]}
                  >
                    {t('applications.detail.candidate_profile.representation_alert', {
                      agency: `${application?.recruiter.company_profile.name}`,
                    })}
                  </Trans>
                </Typography>
              </Stack>
            </Card>
          </Grid>
        )}

        <Grid xs={12} px={0}>
          <Divider sx={{ borderStyle: 'dashed' }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent={{ xs: 'center', sm: "space-between" }} alignItems={{ xs: 'start', sm: "center" }} spacing={2} pt={2}>

            {renderTabs}

            {
              !isMobile && (
                <Stack direction="row" alignItems="center" spacing={1}>



                  {
                    // hide application source for clients and if not the manager of the application
                    // https://jobtetris.atlassian.net/jira/software/c/projects/JT1/boards/2?selectedIssue=JT1-1160
                    (application?.apply_source?.source && application?.manager.id === context?.organization?.id) && (
                      <Stack sx={{ px: 2 }} direction="column" alignItems="end" justifyContent="space-between" gap={.5}>
                        <Typography variant='caption'>{t('applications.detail.date_and_source')}</Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                          <Trans>
                            {t('applications.detail.applied_via', { source: _.capitalize(application.apply_source?.source), date: application?.created_at ? new Date(application.created_at).toLocaleDateString() : '' })}
                          </Trans>
                        </Typography>
                      </Stack>
                    )
                  }

                  {
                    (application && !application?.apply_source?.source && application?.manager.id === context?.organization?.id) && (
                      <Stack sx={{ px: 2 }} direction="column" alignItems="end" justifyContent="space-between" gap={.5}>
                        <Typography variant='caption'>{t('applications.detail.date')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                          <Trans>
                            {t('applications.detail.applied_via_no_source', { date: application ? new Date(application.created_at).toLocaleDateString() : '' })}
                          </Trans>
                        </Typography>
                      </Stack>
                    )
                  }

                  {
                    application?.manager.id === context?.organization?.id && (
                      <Divider orientation="vertical" flexItem />
                    )
                  }

                  {
                    application?.stage === ApplicationStage.INTERVIEW && application?.interview_phase && (
                      <>
                        <Stack sx={{ px: 2 }} direction="column" alignItems="end" justifyContent="space-between" gap={.5}>
                          <Typography variant='caption'>{t('applications.detail.interview_phase')}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {t('applications.interview_phase_modal.phase_label', { phase: application.interview_phase })}
                          </Typography>
                        </Stack>
                        <Divider orientation="vertical" flexItem />
                      </>
                    )
                  }

                  <Stack sx={{ px: 2 }} direction="column" alignItems="end" justifyContent="space-between" gap={.5}>
                    <Typography variant='caption'>{t('applications.detail.rating')}</Typography>
                    <StarRating rating={application?.rating as number} onSelectExcitementRating={(rating) => handleRatingChange(application?.id as string, rating)} />
                  </Stack>

                </Stack>
              )
            }

          </Stack>
        </Grid>

        {application?.stage === ApplicationStage.WITHDRAWN && application?.withdrawal_context && (
          <Grid xs={12}>
            <WithdrawalContextAlert
              context={application?.withdrawal_context as WithdrawalContext}
            />
          </Grid>
        )}

        <Grid xs={12}>
          {isLoading ? (
            <Box paddingTop={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {application ? (
                <>
                  {currentTab === TabValue.PROFILE && (
                    <ApplicationCandidateProfile
                      application={application}
                      profileId={application.attached_profile.id as string}
                      managerCandidateThreadId={getCandidateThread()}
                      managerClientThreadId={getClientThread()}
                      managerClientThreadVisibility={application.manager_client_thread?.visibility}
                      internalThreadId={application.internal_thread?.id}
                    />
                  )}

                  {
                    currentTab === TabValue.RESUME &&
                    <Stack sx={{ px: 3, flexGrow: 1, height: '100vh' }} >
                      {resumeDocument ? (
                        <Box sx={{ height: '100%' }} >

                          <DocumentViewer fileUrl={resumeDocument.document.public_path} />

                        </Box>
                      ) : (
                        <Typography sx={{ py: 5 }} align="center" variant="body2">
                          {t('profile.documents.no_resume')}
                        </Typography>
                      )}
                    </Stack>
                  }

                  {currentTab === TabValue.DOCUMENTS && (
                    <CandidateProfileDocumentsView
                      profileId={application.attached_profile.id}
                      onSaveToProfile={handleSaveToProfile}
                      guestSharing
                    />
                  )}

                  {currentTab === TabValue.SCREENING && (
                    <ApplicationScreeningSubmissionView
                      applicationId={applicationId as string}
                      jobId={job_id as string}
                    />
                  )}

                  {currentTab === TabValue.REFERENCES && (
                    <ApplicationReferencesView applicationId={application.id as string} />
                  )}


                </>
              ) : (
                <Box paddingTop={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="subtitle2" color={theme.palette.grey[600]}>
                    {t('applications.detail.candidate_profile.failed')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid >

      <CustomPopover open={stagePopover.open} onClose={stagePopover.onClose}>
        <Stack spacing={1}>
          {RecruiterApplicationStages.filter((stage) => stage !== ApplicationStage.WITHDRAWN).map((stage) => (
            <MenuItem
              key={stage}
              onClick={() => handleProgressStage(stage)}
              disabled={stageDisabled(
                stage,
                application?.requested_stage ?? (application?.stage as ApplicationStage)
              )}
            >
              {t(`enums.application_stage.${stage}`)}
            </MenuItem>
          ))}
        </Stack>
      </CustomPopover>

      <ConfirmDialog
        open={confirmRequestStageChange.value}
        onClose={confirmRequestStageChange.onFalse}
        title={t('common.confirm')}
        content={getConfirmStageChangeRequestContentMessage(requestedStage as ApplicationStage)}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => onRequestProgressApplication(requestedStage as ApplicationStage)}
          >
            {t('common.confirm')}
          </Button>
        }
      />

      <ConfirmDialog
        open={confirmStageChange.value}
        onClose={confirmStageChange.onFalse}
        title={t('common.confirm')}
        content={getConfirmStageChangeContentMessage(
          requestedStage as ApplicationStage,
          tenantType,
          !!application?.sent_to_client_at
        )}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => onProgressApplication(requestedStage as ApplicationStage)}
          >
            {t('common.confirm')}
          </Button>
        }
      />

      <OffersListModal
        open={showOffersModal.value}
        applicationId={applicationId as string}
        onClose={showOffersModal.onFalse}
      />

      {
        applicationId && (
          <ResourceRequestModal
            open={infoRequestInitiateModalControl.value}
            onClose={infoRequestInitiateModalControl.onFalse}
            applicationId={applicationId as string}
          />
        )
      }

      {
        applicationId && application && (
          <ResourceRequestsListModal
            open={showOutgoingResourceRequests.value}
            onClose={showOutgoingResourceRequests.onFalse}
            target="outgoing"
            requests={resourceRequests}
            pageOptions={resourceRequestPageOptions}
            onChangePage={onChangeResourceRequestPage}
            profileId={application.attached_profile.id as string}
          />
        )
      }

      <ApplicationProfileImporterDialog open={profileImporterControl.value} onClose={profileImporterControl.onFalse} applicationId={application?.id as string} />

      {
        applicationId && (
          <InterviewListModal
            applicationId={applicationId as string}
            open={interviewListModalControl.value}
            onClose={interviewListModalControl.onFalse}
          />
        )
      }

      {
        applicationId && (
          <ReferenceRequestListModal
            applicationId={applicationId as string}
            open={referenceRequestListModalControl.value}
            onClose={referenceRequestListModalControl.onFalse}
          />
        )
      }

      {
        applicationId && (
          <RequestReferenceModal
            applicationId={applicationId as string}
            open={referenceInitiateModalControl.value}
            onClose={referenceInitiateModalControl.onFalse}
          />
        )
      }

      {
        application && (
          <ApplicationRejectionConfirm
            open={rejectApplicationControl.value}
            onClose={onCancelRejectApplication}
            onConfirm={onRejectApplication}
            applications={[application.id] as string[]}
          />
        )
      }

      {
        applicationId && (
          <InterviewPhaseModal
            open={showInterviewPhaseModal.value}
            onClose={showInterviewPhaseModal.onFalse}
            applicationId={applicationId as string}
          />
        )
      }

    </>
  );
}
